import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Copy,
  Globe,
  Info,
  Lock,
  NotebookPen,
  RotateCcw,
  Users,
  AudioLines,
} from 'lucide-react';
import { Meeting } from '../../types';
import type { TranscribeLanguage } from '../../global';
import { LiveTranscriptStream } from './LiveTranscriptStream';
import { RecordingAskBar } from './RecordingAskBar';
import type { RecordingSession } from './useRecordingSession';
import { displaySpeaker } from './speakerLabels';
import { t, useLanguage } from '../../lib/i18n';

type Tab = 'notes' | 'transcript';

/**
 * `Record<...>` olarak yazıldıkları için, tip birliğine yeni bir değer
 * eklendiğinde bu listeler derlenmez — etiketler sessizce eksik kalmaz.
 * Seçili arayüz dilinde üretildikleri için her çizimde çağrılıyorlar.
 */
const languageLabels = (): Record<TranscribeLanguage, string> => ({
  auto: t('Otomatik', 'Automatic'),
  tr: t('Türkçe', 'Turkish'),
  en: t('İngilizce', 'English'),
});

const spaceLabels = (): Record<Meeting['space'], string> => ({
  private: t('Özel', 'Private'),
  shared: t('Paylaşılan', 'Shared'),
});

/** Rozet görünümlü açılır liste — yerel `<select>`, klavyeyle de çalışsın diye. */
function Chip<T extends string>({
  value,
  options,
  onChange,
  icon,
  label,
  isDarkMode,
}: {
  value: T;
  options: Record<T, string>;
  onChange: (next: T) => void;
  icon: React.ReactNode;
  label: string;
  isDarkMode: boolean;
}) {
  return (
    <div
      className={`relative flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-full border text-[12.5px] font-medium transition-colors focus-within:border-[#3AC8E0] ${
        isDarkMode
          ? 'bg-[#19191C] border-[#3B3B3F] text-neutral-300 hover:border-[#4A4A50]'
          : 'bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300'
      }`}
    >
      <span className="text-neutral-500">{icon}</span>
      <span>{options[value]}</span>
      <ChevronDown className="w-3.5 h-3.5 text-neutral-500" />
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="absolute inset-0 opacity-0 cursor-pointer"
      >
        {(Object.keys(options) as T[]).map((key) => (
          <option key={key} value={key}>
            {options[key]}
          </option>
        ))}
      </select>
    </div>
  );
}

interface RecordingViewProps {
  session: RecordingSession;
  isDarkMode: boolean;
  /**
   * Ctrl+Alt+Shift+N her basıldığında artan sayaç. Değiştiğinde "Notlar"
   * sekmesi açılıp not alanına odaklanılıyor (bkz. services/behavior.ts).
   */
  focusNotesSignal?: number;
}

/**
 * Kayıt sayfası. Eskiden bu ekran küçük bir modaldı; artık tam sayfa —
 * konuşulan cümleler tamamlandıkça teker teker akıyor, kenar çubuğu yerinde
 * kalıyor ve kayıt sürerken başka sayfalara geçilebiliyor (oturum App
 * seviyesinde yaşıyor, bkz. useRecordingSession).
 *
 * Düzen üç bölgeye ayrılmış durumda: sabit başlık bloğu, kalan yüksekliğin
 * tamamını dolduran içerik ve sayfaya yapışık soru çubuğu. Ortadaki bölge
 * `flex-1 min-h-0` olduğu için içerik azken de sayfanın altında ölü bir
 * boşluk kalmıyor — boş durum o alanın ortasına oturuyor.
 */
export const RecordingView: React.FC<RecordingViewProps> = ({ session, isDarkMode, focusNotesSignal = 0 }) => {
  const { language } = useLanguage();
  const [tab, setTab] = useState<Tab>('transcript');
  const [copied, setCopied] = useState(false);

  const transcriptText = useMemo(
    () => session.liveTranscript.map((s) => `${displaySpeaker(s.speaker)}: ${s.text}`).join('\n'),
    // Konuşmacı adları seçili dilde kopyalansın.
    [session.liveTranscript, language]
  );

  // Not kısayolu: sekmeyi değiştirip alan çizildikten sonra odaklan.
  useEffect(() => {
    if (!focusNotesSignal) return;
    setTab('notes');
    const timer = window.setTimeout(() => document.getElementById('recording-notes')?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [focusNotesSignal]);

  const copyTranscript = () => {
    if (!transcriptText) return;
    navigator.clipboard.writeText(transcriptText).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }, (err) => console.error('[clipboard] kopyalanamadı:', err));
  };

  const tabClass = (isSelected: boolean) =>
    `flex items-center gap-2 pb-2.5 pt-1 px-1 text-[13.5px] font-semibold transition-colors border-b-2 -mb-px ${
      isSelected
        ? 'border-[#3AC8E0] text-[#46CBE2]'
        : `border-transparent ${isDarkMode ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-500 hover:text-neutral-800'}`
    }`;

  const countClass = (isSelected: boolean) =>
    `text-[11px] font-mono px-1.5 py-0.5 rounded-full ${
      isSelected
        ? 'bg-[#1DA4BA]/15 text-[#46CBE2]'
        : isDarkMode ? 'bg-[#19191C] text-neutral-500' : 'bg-neutral-100 text-neutral-500'
    }`;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* — Sabit başlık bloğu — */}
      <div className="shrink-0 w-full max-w-3xl mx-auto px-6 pt-7">
        <input
          id="recording-title"
          value={session.title}
          onChange={(e) => session.setTitle(e.target.value)}
          aria-label={t('Kayıt başlığı', 'Recording title')}
          placeholder={t('Yeni Kayıt', 'New recording')}
          className={`w-full bg-transparent outline-none text-[32px] leading-tight font-bold tracking-tight placeholder:text-neutral-600 ${
            isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
          }`}
        />

        <div className="flex items-center gap-2 mt-3.5">
          <Chip
            value={session.space}
            options={spaceLabels()}
            onChange={session.setSpace}
            icon={session.space === 'private' ? <Lock className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
            label={t('Kaydın alanı', 'Recording space')}
            isDarkMode={isDarkMode}
          />
          <Chip
            value={session.language}
            options={languageLabels()}
            onChange={session.setLanguage}
            icon={<Globe className="w-3.5 h-3.5" />}
            label={t('Transkript dili', 'Transcript language')}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Kayıt sorumluluğu kullanıcıda (Kullanım Koşulları > Kayıt): ilk
            dakikalarda, katılımcılara söylemeyi hatırlat. */}
        {!session.error && session.seconds < 120 && (
          <p className="mt-3 flex items-center gap-1.5 text-[12px] text-neutral-500">
            <Info className="w-3.5 h-3.5 shrink-0" />
            {t(
              'Katılımcılara toplantının kaydedildiğini söylemeyi unutma.',
              'Remember to tell everyone the meeting is being recorded.'
            )}
          </p>
        )}

        {!session.error && session.notice && (
          <div className="mt-4 p-3.5 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-[13px] flex items-start gap-2.5">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">{session.notice}</span>
          </div>
        )}

        {session.error && (
          <div className="mt-4 p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 text-[13px] flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">{session.error}</span>
            {!session.isRecording && (
              <button
                onClick={() => void session.retry()}
                className="flex items-center gap-1.5 shrink-0 font-semibold hover:text-rose-200 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {t('Tekrar dene', 'Try again')}
              </button>
            )}
          </div>
        )}

        {/* Sekmeler — sağda o sekmenin içeriğine ait eylem durur.
            Kopyalama düğmesi bilerek burada: kopyaladığı şeyin (transkript)
            hemen üstünde ve yalnızca kopyalanacak bir şey varken etkin. */}
        <div
          className={`flex items-end justify-between gap-4 mt-6 border-b ${
            isDarkMode ? 'border-[#343438]' : 'border-neutral-200'
          }`}
        >
          <div className="flex items-end gap-5">
            <button onClick={() => setTab('transcript')} className={tabClass(tab === 'transcript')}>
              <AudioLines className="w-4 h-4" />
              {t('Canlı Transkript', 'Live transcript')}
              {session.liveTranscript.length > 0 && (
                <span className={countClass(tab === 'transcript')}>{session.liveTranscript.length}</span>
              )}
            </button>
            <button onClick={() => setTab('notes')} className={tabClass(tab === 'notes')}>
              <NotebookPen className="w-4 h-4" />
              {t('Notlar', 'Notes')}
            </button>
          </div>

          {tab === 'transcript' && (
            <button
              onClick={copyTranscript}
              disabled={!transcriptText}
              title={transcriptText ? t('Transkripti panoya kopyala', 'Copy transcript to clipboard') : t('Kopyalanacak transkript yok', 'No transcript to copy')}
              className={`mb-1.5 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'text-neutral-400 enabled:hover:text-[#F0EEE6] enabled:hover:bg-[#1F1F23]'
                  : 'text-neutral-500 enabled:hover:text-neutral-900 enabled:hover:bg-neutral-100'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">{t('Kopyalandı', 'Copied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>{t('Kopyala', 'Copy')}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* — İçerik: kalan yüksekliğin tamamı — */}
      <div className="flex-1 min-h-0 w-full max-w-3xl mx-auto px-6 flex flex-col">
        {tab === 'transcript' ? (
          <LiveTranscriptStream
            segments={session.liveTranscript}
            pendingLines={session.pendingLines}
            isPaused={session.isPaused}
            isStarting={session.phase === 'starting'}
            isDarkMode={isDarkMode}
          />
        ) : (
          <textarea
            id="recording-notes"
            value={session.notes}
            onChange={(e) => session.setNotes(e.target.value)}
            placeholder={t(
              'Kayıt sırasında aklına geleni yaz…\n\nSatır başına "- " koyarsan madde olur. Buraya yazdıkların kayıt bitince notun içine geçer.',
              'Write down anything that comes to mind during the recording…\n\nStart a line with "- " to make it a bullet. What you write here is added to the note when the recording ends.'
            )}
            className={`flex-1 min-h-0 w-full bg-transparent outline-none resize-none pt-5 pb-4 text-[15px] leading-relaxed placeholder:text-neutral-600 ${
              isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
            }`}
          />
        )}
      </div>

      <RecordingAskBar segments={session.liveTranscript} isDarkMode={isDarkMode} />
    </div>
  );
};
